import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useFabricAnchor, useFabricFrame } from './FlowingFabric';
import { EASE_OUT } from '../motion';
// Sheet indexes follow buildScene: full = [back, hero, strand].
const NOTES_FULL = [
    {
        id: 'voice',
        code: 'A.01',
        title: 'AI voice interview',
        meta: 'Asks role questions, follows up live',
        sheet: 0,
        u: 0.3,
        v: -0.4,
        dir: 'ne',
        wave: true,
    },
    {
        id: 'scoring',
        code: 'A.02',
        title: 'Skill scoring',
        meta: 'Every answer scored against your rubric',
        sheet: 0,
        u: 0.5,
        v: 0,
        dir: 'ne',
    },
    {
        id: 'integrity',
        code: 'A.03',
        title: 'Integrity check',
        meta: 'Flags tab switches & look-aways',
        sheet: 0,
        u: 0.6,
        v: -0.8,
        dir: 'se',
    },
    {
        id: 'report',
        code: 'A.04',
        title: 'Shortlist report',
        meta: 'Score, transcript & recommendation',
        sheet: 1,
        u: 0.55,
        v: -0.4,
        dir: 'ne',
    },
];
// Compact = [back, hero, strand] on tablet (phones show no annotations).
const NOTES_COMPACT = [
    Object.assign(Object.assign({}, NOTES_FULL[0]), { sheet: 0, u: 0.62, v: -0.8, dir: 'ne' }),
    Object.assign(Object.assign({}, NOTES_FULL[1]), { sheet: 0, u: 0.75, v: -0.4, dir: 'sw' }),
];
const LENS_FULL = { sheet: 1, u: 0.48, v: 0.6 };
const LENS_COMPACT = { sheet: 1, u: 0.42, v: -0.6 };
const POINTS = [
    [1, 0.3, -0.1],
    [1, 0.36, 0.5],
    [1, 0.42, -0.7],
    [1, 0.5, -0.05],
    [1, 0.6, 0.55],
    [1, 0.24, 0.85],
    [0, 0.22, 0.3],
    [0, 0.42, -0.35],
    [0, 0.54, 0.6],
    [0, 0.7, -0.3],
];
const LEADER = {
    ne: { d: 'M0 0 L26 -26 L70 -26', x: 78, y: -26 },
    se: { d: 'M0 0 L26 26 L70 26', x: 78, y: 26 },
    nw: { d: 'M0 0 L-26 -26 L-70 -26', x: -78, y: -26 },
    sw: { d: 'M0 0 L-26 26 L-70 26', x: -78, y: 26 },
};
function Wave() {
    return (_jsx("span", { className: 'vx-note__wave', "aria-hidden": 'true', children: Array.from({ length: 18 }, (_, i) => (_jsx("i", { style: { animationDelay: `${(i * 0.11) % 1.3}s` } }, i))) }));
}
const FLIP = { ne: 'nw', se: 'sw', nw: 'ne', sw: 'se' };
/**
 * Left edge of the form column when it sits beside the cloth (desktop), else
 * the viewport edge. Labels must stay left of it — the column is a solid
 * surface, so anything past this line would be cut off.
 */
function usableRight() {
    var _a;
    const main = (_a = document.querySelector('.vx-auth__main')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
    return main && main.top < 160 ? main.left : window.innerWidth;
}
function Annotation({ note, index, start }) {
    const ref = useFabricAnchor(note.sheet, note.u, note.v);
    const labelRef = useRef(null);
    // East-facing labels flip west when they would run under the form column
    // (the sign-up column is wider). Re-checked slowly as the cloth drifts,
    // with a margin so a label near the edge doesn't flicker between sides.
    const [flipped, setFlipped] = useState(false);
    useEffect(() => {
        const east = note.dir === 'ne' || note.dir === 'se';
        if (!east)
            return undefined;
        const check = () => {
            var _a, _b, _c;
            const anchor = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const label = (_c = (_b = labelRef.current) === null || _b === void 0 ? void 0 : _b.firstElementChild) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
            if (!anchor || !label)
                return;
            const reach = anchor.left + LEADER[note.dir].x + label.width;
            const limit = usableRight() - 16;
            setFlipped((f) => (f ? reach > limit - 40 : reach > limit));
        };
        const t = window.setTimeout(check, 400);
        const id = window.setInterval(check, 1200);
        window.addEventListener('resize', check);
        return () => {
            window.clearTimeout(t);
            window.clearInterval(id);
            window.removeEventListener('resize', check);
        };
    }, [note.dir, ref]);
    const dir = flipped ? FLIP[note.dir] : note.dir;
    const leader = LEADER[dir];
    const west = dir === 'nw' || dir === 'sw';
    const delay = start + index * 0.18;
    return (_jsxs("div", { ref: ref, className: 'vx-note', "data-note": note.id, children: [_jsx(motion.span, { className: 'vx-note__dot', initial: { scale: 0 }, animate: { scale: 1 }, transition: { duration: 0.8, delay, ease: EASE_OUT } }), _jsx("svg", { className: 'vx-note__leader', width: '1', height: '1', overflow: 'visible', children: _jsx(motion.path, { d: leader.d, initial: { pathLength: 0 }, animate: { pathLength: 1 }, transition: { duration: 1.1, delay: delay + 0.15, ease: EASE_OUT } }) }), _jsx("div", { ref: labelRef, className: `vx-note__label${west ? ' is-west' : ''}`, style: { left: leader.x, top: leader.y }, children: _jsxs(motion.div, { initial: { opacity: 0, x: west ? 8 : -8 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.9, delay: delay + 0.5, ease: EASE_OUT }, children: [_jsx("span", { className: 'vx-note__code', children: note.code }), _jsxs("span", { className: 'vx-note__title', children: [note.title, note.wave && _jsx(Wave, {})] }), _jsx("span", { className: 'vx-note__meta', children: note.meta })] }) })] }));
}
function Lens({ at, start }) {
    const ref = useFabricAnchor(at.sheet, at.u, at.v);
    return (_jsxs("div", { ref: ref, className: 'vx-lens', children: [_jsxs(motion.svg, { className: 'vx-lens__rings', viewBox: '-110 -110 220 220', initial: { opacity: 0, scale: 0.86 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 1.6, delay: start, ease: EASE_OUT }, children: [_jsx("circle", { r: '100', className: 'vx-lens__ticks' }), _jsx("circle", { r: '64', className: 'vx-lens__ring' }), _jsx("circle", { r: '82', className: 'vx-lens__scan', pathLength: '100' }), _jsx("path", { d: 'M-108 0 H-92 M92 0 H108 M0 -108 V-92 M0 92 V108', className: 'vx-lens__cross' }), _jsx("circle", { r: '2.5', className: 'vx-lens__core' }), [30, 120, 210, 300].map((deg, i) => {
                        const r = (deg * Math.PI) / 180;
                        return (_jsx("circle", { cx: Math.cos(r) * 100, cy: Math.sin(r) * 100, r: '3.5', className: 'vx-lens__node', style: { animationDelay: `${i * 1.5}s` } }, deg));
                    })] }), _jsxs(motion.span, { className: 'vx-lens__label', initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 1, delay: start + 0.6 }, children: [_jsxs("span", { children: [_jsx("i", { className: 'vx-lens__live' }), " Interview in progress"] }), _jsx("span", { className: 'vx-lens__sub', children: "Candidate answering" })] })] }));
}
function Connections({ at, notes }) {
    const paths = useRef([]);
    useFabricFrame((f) => {
        const [lx, ly] = f.project(at.sheet, at.u, at.v);
        notes.forEach((n, i) => {
            const el = paths.current[i];
            if (!el)
                return;
            const [x, y] = f.project(n.sheet, n.u, n.v);
            // Bow each line away from the straight chord, like a thread under tension.
            const mx = (lx + x) / 2 + (y - ly) * 0.18;
            const my = (ly + y) / 2 - (x - lx) * 0.18;
            el.setAttribute('d', `M${lx.toFixed(1)} ${ly.toFixed(1)} Q${mx.toFixed(1)} ${my.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)}`);
        });
    });
    return (_jsx("svg", { className: 'vx-links', children: notes.map((n, i) => (_jsx("path", { ref: (el) => {
                paths.current[i] = el;
            }, style: { animationDelay: `${i * -1.1}s` } }, n.id))) }));
}
function EvidencePoint({ p, i, start }) {
    const ref = useFabricAnchor(p[0], p[1], p[2], true);
    return (_jsx("span", { ref: ref, className: 'vx-point', children: _jsx("span", { style: { animationDelay: `${start + i * 0.12}s, ${(i * 0.7) % 5}s` } }) }));
}
export function AIAnnotations({ variant, start = 1.4, }) {
    const full = variant === 'full';
    const notes = full ? NOTES_FULL : NOTES_COMPACT;
    const lens = full ? LENS_FULL : LENS_COMPACT;
    return (_jsxs(_Fragment, { children: [_jsx(Connections, { at: lens, notes: notes }), POINTS.filter((p) => full || p[0] === 1).map((p, i) => (_jsx(EvidencePoint, { p: p, i: i, start: start }, `${p[0]}-${p[1]}-${p[2]}`))), _jsx(Lens, { at: lens, start: start }), notes.map((n, i) => (_jsx(Annotation, { note: n, index: i, start: start + 0.3 }, n.id)))] }));
}
